import React, { useState, useEffect } from 'react';
import { RefreshCw, Mail, Calendar, Phone, User, MessageSquare, Clock, Lock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ADMIN_PASSWORD = 'wahr2025';

const Admin = () => {
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contacts');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const auth = localStorage.getItem('admin_authenticated');
    if (auth === 'true') {
      setIsAuthenticated(true);
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('admin_authenticated', 'true');
      setPasswordError('');
      fetchData();
    } else {
      setPasswordError('Falsches Passwort. Bitte versuchen Sie es erneut.');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('admin_authenticated');
    setPassword('');
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [contactsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/bookings`)
      ]);
      
      setContacts(contactsRes.data.contacts || []);
      setBookings(bookingsRes.data.bookings || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBookingDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (!isAuthenticated) {
    return (
      <div style={styles.loginContainer}>
        <div style={styles.loginBox}>
          <div style={styles.loginHeader}>
            <Lock size={48} color="var(--brand-primary)" />
            <h1 style={styles.loginTitle}>WAHR Coaching Admin</h1>
            <p style={styles.loginSubtitle}>Bitte melden Sie sich an</p>
          </div>
          <form onSubmit={handleLogin} style={styles.loginForm}>
            <div style={styles.inputGroup}>
              <label htmlFor="password" style={styles.label}>Passwort</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.passwordInput}
                placeholder="Passwort eingeben"
                autoFocus
              />
            </div>
            {passwordError && (
              <div style={styles.errorMessage}>{passwordError}</div>
            )}
            <button type="submit" className="btn-primary" style={styles.loginButton}>
              Anmelden
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>WAHR Coaching - Admin Dashboard</h1>
        <div style={styles.headerButtons}>
          <button onClick={fetchData} style={styles.refreshButton} disabled={isLoading}>
            <RefreshCw size={20} style={{ marginRight: '8px' }} />
            {isLoading ? 'Lädt...' : 'Aktualisieren'}
          </button>
          <button onClick={handleLogout} style={styles.logoutButton}>
            Abmelden
          </button>
        </div>
      </div>

      <div style={styles.stats}>
        <div style={styles.statCard}>
          <Mail size={24} color="var(--brand-primary)" />
          <div>
            <div style={styles.statValue}>{contacts.length}</div>
            <div style={styles.statLabel}>Kontaktanfragen</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <Calendar size={24} color="var(--brand-primary)" />
          <div>
            <div style={styles.statValue}>{bookings.length}</div>
            <div style={styles.statLabel}>Buchungen</div>
          </div>
        </div>
      </div>

      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('contacts')}
          style={{
            ...styles.tab,
            ...(activeTab === 'contacts' ? styles.tabActive : {})
          }}
        >
          Kontaktanfragen ({contacts.length})
        </button>
        <button
          onClick={() => setActiveTab('bookings')}
          style={{
            ...styles.tab,
            ...(activeTab === 'bookings' ? styles.tabActive : {})
          }}
        >
          Buchungen ({bookings.length})
        </button>
      </div>

      {activeTab === 'contacts' && (
        <div style={styles.content}>
          <h2 style={styles.sectionTitle}>Kontaktanfragen</h2>
          {contacts.length === 0 ? (
            <div style={styles.emptyState}>Noch keine Kontaktanfragen vorhanden</div>
          ) : (
            <div style={styles.list}>
              {contacts.map((contact, index) => (
                <div key={contact.id || index} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardTitle}>
                      <User size={20} color="var(--brand-primary)" />
                      <span>{contact.name}</span>
                    </div>
                    <div style={styles.cardDate}>
                      <Clock size={16} />
                      {formatDate(contact.created_at)}
                    </div>
                  </div>
                  <div style={styles.cardBody}>
                    <div style={styles.cardRow}>
                      <Mail size={18} color="var(--brand-primary)" />
                      <a href={`mailto:${contact.email}`} style={styles.link}>
                        {contact.email}
                      </a>
                    </div>
                    {contact.phone && (
                      <div style={styles.cardRow}>
                        <Phone size={18} color="var(--brand-primary)" />
                        <a href={`tel:${contact.phone}`} style={styles.link}>
                          {contact.phone}
                        </a>
                      </div>
                    )}
                    <div style={styles.messageBox}>
                      <MessageSquare size={18} color="var(--brand-primary)" />
                      <p style={styles.message}>{contact.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'bookings' && (
        <div style={styles.content}>
          <h2 style={styles.sectionTitle}>Terminbuchungen</h2>
          {bookings.length === 0 ? (
            <div style={styles.emptyState}>Noch keine Buchungen vorhanden</div>
          ) : (
            <div style={styles.list}>
              {bookings.map((booking, index) => (
                <div key={booking.id || index} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardTitle}>
                      <User size={20} color="var(--brand-primary)" />
                      <span>{booking.name}</span>
                    </div>
                    <div style={styles.cardDate}>
                      <Clock size={16} />
                      {formatDate(booking.created_at)}
                    </div>
                  </div>
                  <div style={styles.cardBody}>
                    <div style={styles.bookingInfo}>
                      <Calendar size={20} color="var(--brand-primary)" />
                      <div>
                        <div style={styles.bookingDate}>
                          {formatBookingDate(booking.date)}
                        </div>
                        <div style={styles.bookingTime}>
                          Uhrzeit: {booking.time} Uhr
                        </div>
                      </div>
                    </div>
                    <div style={styles.cardRow}>
                      <Mail size={18} color="var(--brand-primary)" />
                      <a href={`mailto:${booking.email}`} style={styles.link}>
                        {booking.email}
                      </a>
                    </div>
                    <div style={styles.cardRow}>
                      <Phone size={18} color="var(--brand-primary)" />
                      <a href={`tel:${booking.phone}`} style={styles.link}>
                        {booking.phone}
                      </a>
                    </div>
                    <div style={styles.statusBadge}>
                      Status: {booking.status === 'confirmed' ? 'Bestätigt' : booking.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  loginContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, var(--bg-page) 0%, var(--bg-subtle) 100%)',
    padding: '20px',
  },
  loginBox: {
    background: 'white',
    borderRadius: '24px',
    padding: '48px',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  },
  loginHeader: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  loginTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    marginTop: '16px',
    marginBottom: '8px',
  },
  loginSubtitle: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
  },
  loginForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  passwordInput: {
    padding: '12px 16px',
    borderRadius: '12px',
    border: '2px solid var(--border-light)',
    fontSize: '16px',
    transition: 'border-color 0.2s ease',
    fontFamily: 'inherit',
  },
  errorMessage: {
    padding: '12px',
    background: '#fee',
    color: '#c33',
    borderRadius: '8px',
    fontSize: '14px',
    textAlign: 'center',
  },
  loginButton: {
    width: '100%',
    marginTop: '8px',
  },
  container: {
    minHeight: '100vh',
    background: 'var(--bg-page)',
    padding: '40px 20px',
  },
  header: {
    maxWidth: '1200px',
    margin: '0 auto 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
    fontWeight: '700',
    color: 'var(--text-primary)',
  },
  headerButtons: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 24px',
    background: 'var(--brand-primary)',
    color: 'white',
    border: 'none',
    borderRadius: '25px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  logoutButton: {
    padding: '12px 24px',
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '2px solid var(--border-medium)',
    borderRadius: '25px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  stats: {
    maxWidth: '1200px',
    margin: '0 auto 32px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
  },
  statCard: {
    background: 'white',
    padding: '24px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--text-primary)',
  },
  statLabel: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
  },
  tabs: {
    maxWidth: '1200px',
    margin: '0 auto 32px',
    display: 'flex',
    gap: '8px',
    borderBottom: '2px solid var(--border-light)',
  },
  tab: {
    padding: '12px 24px',
    background: 'transparent',
    border: 'none',
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    transition: 'all 0.2s ease',
  },
  tabActive: {
    color: 'var(--brand-primary)',
    borderBottom: '3px solid var(--brand-primary)',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '24px',
    color: 'var(--text-primary)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: '1px solid var(--border-light)',
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  cardDate: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    color: 'var(--text-light)',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  cardRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  link: {
    color: 'var(--brand-primary)',
    textDecoration: 'none',
  },
  messageBox: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
    padding: '12px',
    background: 'var(--bg-subtle)',
    borderRadius: '8px',
    marginTop: '8px',
  },
  message: {
    margin: 0,
    fontSize: '14px',
    lineHeight: '1.5',
    color: 'var(--text-secondary)',
  },
  bookingInfo: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    background: 'rgba(75, 149, 209, 0.1)',
    borderRadius: '8px',
    marginBottom: '8px',
  },
  bookingDate: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  bookingTime: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    background: 'rgba(75, 149, 209, 0.1)',
    color: 'var(--brand-primary)',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    marginTop: '8px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px',
    fontSize: '16px',
    color: 'var(--text-secondary)',
    background: 'white',
    borderRadius: '16px',
  },
};

export default Admin;
